/**
 * Chassis token art — battlefield tokens keyed by frame (chassis) id,
 * served from `public/tokens/`.
 *
 * Distinct from frame *box* art (`public/frames/`): those
 * are the large diagonal illustrations the compendium shows; these are the
 * square token cards (the chassis art inside a red hexagon frame) a deployed mech
 * wears on the board, picked by its selected chassis. Mirrored locally (from the community "Mech Tokens" set) so
 * the map isn't hostage to a third-party host's reachability/CORS.
 *
 * Resolution: a frame with a bespoke token gets it; anything else (the six
 * frames the set lacks — atlas/caliban/kobold/lich/everest/sunzi — and any NPC
 * or unidentified combatant) falls back to the size-keyed generic ring.
 *
 * {@link tokenImageFor} is the board's resolver: it layers the player's uploaded art, box art and
 * disclosure on top of these tokens, and is what `pixiCombatBoard.ts` draws from.
 */
import { resolveMechArtUrl } from "./mechArt";

/**
 * Frame ids with a bespoke chassis token in `public/tokens/mf_<id>.png`.
 * Kept in lockstep with the files on disk by `tokenArt.test.ts`.
 */
export const CHASSIS_TOKEN_IDS: ReadonlySet<string> = new Set([
  "mf_balor",
  "mf_barbarossa",
  "mf_black_witch",
  "mf_blackbeard",
  "mf_deaths_head",
  "mf_drake",
  "mf_dusk_wing",
  "mf_genghis",
  "mf_goblin",
  "mf_gorgon",
  "mf_hydra",
  "mf_iskander",
  "mf_lancaster",
  "mf_manticore",
  "mf_metalmark",
  "mf_minotaur",
  "mf_monarch",
  "mf_mourning_cloak",
  "mf_napoleon",
  "mf_nelson",
  "mf_pegasus",
  "mf_raleigh",
  "mf_saladin",
  "mf_sherman",
  "mf_swallowtail",
  "mf_tokugawa",
  "mf_tortuga",
  "mf_vlad",
]);

/**
 * NPC class ids with generated class art in `public/tokens/npc/<id>.webp`: one full-body figure per
 * class, keyed onto a transparent background, made with the house method in
 * `agents/memories/art-generation.md` (contact sheet and prompts in the workspace's
 * `projects/art-pass/`). The board draws it for an unmasked NPC of that class, and the compendium's
 * class record shows it as its hero. Kept in lockstep with the files on disk by `tokenArt.test.ts`.
 */
export const NPC_CLASS_TOKEN_IDS: ReadonlySet<string> = new Set([
  "npcc_ace",
  "npcc_aegis",
  "npcc_archer",
  "npcc_assassin",
  "npcc_assault",
  "npcc_barricade",
  "npcc_bastion",
  "npcc_berserker",
  "npcc_bombard",
  "npcc_breacher",
  "npcc_cataphract",
  "npcc_demolisher",
  "npcc_engineer",
  "npcc_goliath",
  "npcc_hive",
  "npcc_hornet",
  "npcc_human",
  "npcc_mirage",
  "npcc_monstrosity",
  "npcc_operator",
  "npcc_priest",
  "npcc_pyro",
  "npcc_rainmaker",
  "npcc_ronin",
  "npcc_scourer",
  "npcc_scout",
  "npcc_seeder",
  "npcc_sentinel",
  "npcc_sniper",
  "npcc_specter",
  "npcc_squad",
  "npcc_support",
  "npcc_terraforming_rig",
  "npcc_witch",
]);

/** The class art URL for an NPC class id, or `undefined` for a class without art. */
export function npcClassArtUrl(classId: string | null | undefined): string | undefined {
  return classId && NPC_CLASS_TOKEN_IDS.has(classId) ? `/tokens/npc/${classId}.webp` : undefined;
}

/** The on-foot pilot token, for pilot-kind combatants. */
export const PILOT_TOKEN_URL = "/tokens/pilot.png";

/**
 * Generic rings the set ships, by Lancer size class. An arbitrary size snaps to
 * the nearest available ring (0.5 for sub-1, then clamped 1..4).
 */
function genericSizeKey(size: number): string {
  if (size <= 0.5) return "0.5";
  return String(Math.min(4, Math.max(1, Math.round(size))));
}

/**
 * The token-art URL for a mech's selected chassis: its bespoke token when the
 * set has one, else the generic ring for `size` (default Size 1). `frameId` is
 * the compendium frame id — e.g. a combatant's `sourceRef`, or `MechData.frameId`.
 */
export function tokenArtUrl(frameId: string | null | undefined, size = 1): string {
  if (frameId && CHASSIS_TOKEN_IDS.has(frameId)) return `/tokens/${frameId}.png`;
  return `/tokens/generic_size_${genericSizeKey(size)}.png`;
}

// ── Per-combatant token image resolution (`projects/token-art.md` Phase 1) ──────────────────────

/**
 * What a combatant's board token shows. `kind` is the coarse class the tracker names; `tier` is the
 * exact rung of the resolution ladder that answered, which is what the tests pin and what the
 * renderer reads for the one per-tier drawing difference ({@link TokenImage.framed}).
 *
 * `url` is `null` exactly when `kind === "generic"`: the generic tier is drawn as a vector ring in
 * the side colour, carrying {@link TokenImage.glyph} when there is one. The vendored
 * `generic_size_*.png` files are NOT used for it. They are hex-cluster illustrations of particular
 * mechs, cut for a hex grid; on this square board they would give every NPC the same stranger's
 * mech, and a masked hostile real-looking art its mask is supposed to withhold.
 */
export type TokenImage = {
  kind: "image" | "chassis" | "generic";
  tier: "gm-set" | "mech-upload" | "chassis-token" | "box-art" | "pilot-portrait" | "pilot-token" | "npc-class" | "generic" | "masked";
  url: string | null;
  /**
   * The image is a vendored `public/tokens/` PNG with the set's red hexagon frame baked into it.
   * The renderer zooms it so the circle crop cuts the frame off (a red border would read as
   * "hostile" on a friendly token).
   */
  framed: boolean;
  /** A short role mark for the generic ring (an NPC's class role, e.g. `STR`). Never set when masked. */
  glyph?: string;
};

/** The roster slice {@link tokenImageFor} reads — structurally a `PilotData`, trimmed to the art fields. */
export type TokenRosterPilot = {
  image?: string;
  mechs: readonly { id: string; frameId: string | null; image?: string }[];
};

/** The combatant fields {@link tokenImageFor} reads. A full `Combatant` satisfies it. */
export type TokenSubject = {
  id: string;
  kind: "mech" | "npc" | "pilot" | "drone" | "deployable" | "object";
  sourceRef?: string;
  pilotId?: string;
  sourcePrintedMechId?: string;
  /** The GM-set override (`Combatant.tokenImage`, written by `set_token_image`). */
  tokenImage?: string;
};

/**
 * Everything outside the combatant that token resolution depends on.
 *
 * - `pilots`: the live roster's pilots by id (`RosterState.pilots`), or `undefined` before the
 *   roster socket has delivered. Mechs keep resolving live through it, so a portrait uploaded
 *   mid-fight reaches the board.
 * - `isMasked`: whether THIS viewer sees the combatant through a disclosure mask (a hostile at the
 *   `class`/`loadout` tier). Built from the real projection by `buildTokenImageCtx` (`lib/encounter/tokenImageCtx.ts`).
 * - `npcRole`: an NPC class id to its role (`npc_class.role`), from the compendium.
 */
export type TokenImageCtx = {
  pilots: Readonly<Record<string, TokenRosterPilot>> | undefined;
  isMasked: (id: string) => boolean;
  npcRole?: (classId: string) => string | undefined;
};

const GENERIC: TokenImage = { kind: "generic", tier: "generic", url: null, framed: false };

/** An NPC role as a three-letter ring mark: `striker` → `STR`. */
function roleGlyph(role: string | undefined): string | undefined {
  const t = role?.trim();
  return t ? t.slice(0, 3).toUpperCase() : undefined;
}

/**
 * Resolve the image a combatant's board token shows, for one viewer.
 *
 * - **Masked** (the viewer sees it through a disclosure mask): the generic ring with no glyph,
 *   whatever the unit is. Checked first, so no later rung can leak art or a class role — the
 *   GM-set image included.
 * - **GM-set image** (`Combatant.tokenImage`, `set_token_image`): wins for any kind of unit,
 *   ahead of the roster and chassis art.
 * - **Mech**: the player's uploaded `mech.image`, then the chassis token
 *   (`CHASSIS_TOKEN_IDS`), then the chassis box art (`resolveMechArtUrl`'s frame tier), then the generic ring. The roster mech is
 *   found by `pilotId` + `sourcePrintedMechId`; a dangling pair (mech deleted or reprinted since
 *   deploy) skips the upload rung and still resolves the chassis from `sourceRef`, the frame the
 *   combatant was spawned as.
 * - **Pilot**: the uploaded pilot portrait (`sourceRef` is the roster pilot id), then the stock
 *   pilot token.
 * - **NPC**: its class art ({@link NPC_CLASS_TOKEN_IDS}), then the generic ring carrying the class
 *   role as its glyph when the compendium knows it.
 * - **Anything else** (drone, object, deployable): the generic ring.
 */
export function tokenImageFor(c: TokenSubject, ctx: TokenImageCtx): TokenImage {
  if (ctx.isMasked(c.id)) return { kind: "generic", tier: "masked", url: null, framed: false };
  if (c.tokenImage) return { kind: "image", tier: "gm-set", url: c.tokenImage, framed: false };

  if (c.kind === "mech") {
    const pilot = c.pilotId !== undefined ? ctx.pilots?.[c.pilotId] : undefined;
    const mech = pilot?.mechs.find((m) => m.id === c.sourcePrintedMechId);
    if (mech?.image) return { kind: "image", tier: "mech-upload", url: mech.image, framed: false };
    const frameId = c.sourceRef;
    if (frameId && CHASSIS_TOKEN_IDS.has(frameId)) return { kind: "chassis", tier: "chassis-token", url: `/tokens/${frameId}.png`, framed: true };
    const boxArt = frameId ? resolveMechArtUrl({ frameId }) : undefined;
    if (boxArt) return { kind: "chassis", tier: "box-art", url: boxArt, framed: false };
    return GENERIC;
  }

  if (c.kind === "pilot") {
    const portrait = c.sourceRef !== undefined ? ctx.pilots?.[c.sourceRef]?.image : undefined;
    if (portrait) return { kind: "image", tier: "pilot-portrait", url: portrait, framed: false };
    return { kind: "chassis", tier: "pilot-token", url: PILOT_TOKEN_URL, framed: true };
  }

  if (c.kind === "npc" && c.sourceRef) {
    const classArt = npcClassArtUrl(c.sourceRef);
    if (classArt) return { kind: "image", tier: "npc-class", url: classArt, framed: false };
    const glyph = roleGlyph(ctx.npcRole?.(c.sourceRef));
    if (glyph) return { ...GENERIC, glyph };
  }
  return GENERIC;
}

/** The chassis-token URL for each frame id that has one — the board's own art, for the precache. */
export function chassisTokenUrls(frameIds: readonly string[]): string[] {
  return frameIds.filter((id) => CHASSIS_TOKEN_IDS.has(id)).map((id) => `/tokens/${id}.png`);
}
